import { FunctionComponent, useMemo } from 'react';
import { Box, Paper, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { ScatterChart } from '@mui/x-charts/ScatterChart';
import { ChartsReferenceLine } from '@mui/x-charts';
import { TooltipButton } from '../../miscellaneous-components/tooltip-button';
import gaiaTheme from '../../../../root-components/theme';
import { IHotspotMatrixPoint } from '../../../../shared/services/analytics/hotspots.model';

const QUADRANT_META: Record<string, { label: string; paletteKey: string }> = {
  priority: { label: 'Priority', paletteKey: 'error.main' },
  maintain: { label: 'Maintain', paletteKey: 'warning.main' },
  watch: { label: 'Watch', paletteKey: 'info.main' },
  low: { label: 'Low priority', paletteKey: 'success.main' },
  large: { label: 'Larger', paletteKey: 'primary.main' },
  small: { label: 'Smaller', paletteKey: 'lightGrey.dark' },
};
// Order quadrants so the most important sit first in the legend.
const QUADRANT_ORDER = ['priority', 'maintain', 'watch', 'low', 'large', 'small'];

const fmtTco2e = new Intl.NumberFormat('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Plotted x is clamped so an outlier trend (or a brand-new contributor) does not
// blow out the axis; the tooltip always reports the true value.
const X_CLAMP = 150;
const clampTrend = (trend: number | null): number => {
  if (trend === null || Number.isNaN(trend)) return 0;
  return Math.max(-X_CLAMP, Math.min(X_CLAMP, trend));
};

/**
 * Prioritisation matrix (scatter): impact (share of footprint, y) vs trend
 * (period-over-period %, x), points coloured by priority quadrant. Reference
 * lines split the plot into the four quadrants.
 */
export const GraphBubbleChart: FunctionComponent<{
  title: string;
  tooltip?: string;
  points: IHotspotMatrixPoint[];
  impactMedian: number;
  hasComparison: boolean;
  chartHeight?: number;
  paperHeight?: number | string;
  onSelect?: (point: IHotspotMatrixPoint) => void;
}> = ({ title, tooltip, points, impactMedian, hasComparison, chartHeight = 420, paperHeight = '100%', onSelect }) => {
  const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('sm'));
  const theme = useTheme();

  const resolveColor = (paletteKey: string): string => {
    const [group, shade] = paletteKey.split('.');
    return (theme.palette as any)?.[group]?.[shade] || theme.palette.primary.main;
  };

  const { series, idToPoint } = useMemo(() => {
    const idToPoint = new Map<string, IHotspotMatrixPoint>();
    const byQuadrant = new Map<string, Array<{ x: number; y: number; id: string }>>();
    points.forEach((point, index) => {
      const id = `${point.quadrant}-${index}`;
      idToPoint.set(id, point);
      const list = byQuadrant.get(point.quadrant) || [];
      list.push({ x: clampTrend(point.trendPct), y: point.impactPct, id });
      byQuadrant.set(point.quadrant, list);
    });
    const series = QUADRANT_ORDER
      .filter((quadrant) => byQuadrant.has(quadrant))
      .map((quadrant) => ({
        type: 'scatter' as const,
        id: quadrant,
        label: QUADRANT_META[quadrant]?.label || quadrant,
        color: resolveColor(QUADRANT_META[quadrant]?.paletteKey || 'primary.main'),
        markerSize: 7,
        data: byQuadrant.get(quadrant)!,
        valueFormatter: (value: { x: number; y: number; id: string | number }) => {
          const point = idToPoint.get(String(value.id));
          if (!point) return '';
          const trend = point.trendPct === null
            ? 'no prior data'
            : `${point.trendPct > 0 ? '+' : ''}${point.trendPct.toFixed(1)}%`;
          return `${point.label}: ${fmtTco2e.format(point.value)} tCO₂e · ${point.impactPct.toFixed(1)}% share · trend ${point.isNew ? 'new' : trend}`;
        },
      }));
    return { series, idToPoint };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [points, theme]);

  const handleItemClick = (_event: any, item: { seriesId: string | number; dataIndex: number }) => {
    if (!onSelect) return;
    const clickedSeries = series.find((s) => s.id === item.seriesId);
    const dataPoint = clickedSeries?.data[item.dataIndex];
    const point = dataPoint ? idToPoint.get(String(dataPoint.id)) : undefined;
    if (point) onSelect(point);
  };

  return (
    <Paper variant='outlined' sx={{ p: 4, height: paperHeight, display: 'flex', flexDirection: 'column' }}>
      <Stack direction='row' alignItems='center'>
        <Typography variant='subtitle1' sx={{ flex: 1 }}>
          <b>{title}</b>
        </Typography>
        {tooltip && !isMobileMode && <TooltipButton text={tooltip} noPadding={true} placement='right' />}
      </Stack>
      <Typography variant='caption' color='text.secondary'>
        {hasComparison
          ? 'Top-right = large & growing (highest priority). Top-left = large & shrinking. Bottom = smaller contributors.'
          : 'No comparison period available. Points show contribution only (no trend).'}
      </Typography>
      {points.length === 0 ? (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, minHeight: chartHeight }}>
          <Typography color='text.secondary'>No data available.</Typography>
        </Box>
      ) : (
        <Box sx={{ width: '100%', mt: 1 }}>
          <Box sx={{ width: '100%', height: chartHeight }}>
          <ScatterChart
            height={chartHeight}
            series={series as any}
            onItemClick={onSelect ? (handleItemClick as any) : undefined}
            sx={onSelect ? { '& .MuiScatterChart-root, & circle': { cursor: 'pointer' } } : undefined}
            xAxis={[{
              label: hasComparison ? 'Trend vs comparison period (%)' : 'Trend (unavailable)',
              min: hasComparison ? -X_CLAMP : -1,
              max: hasComparison ? X_CLAMP : 1,
            }]}
            yAxis={[{ label: 'Share of footprint (%)', min: 0 }]}
            margin={{ left: 64, right: 24, top: 16, bottom: 48 }}
            slotProps={{ legend: { hidden: true } }}
            grid={{ horizontal: true, vertical: true }}
          >
            {impactMedian > 0 && (
              <ChartsReferenceLine
                y={impactMedian}
                lineStyle={{ strokeDasharray: '5 5', stroke: theme.palette.text.secondary }}
                label='Median impact'
                labelAlign='start'
              />
            )}
            {hasComparison && (
              <ChartsReferenceLine
                x={0}
                lineStyle={{ strokeDasharray: '5 5', stroke: theme.palette.text.secondary }}
                label='No change'
                labelAlign='end'
              />
            )}
          </ScatterChart>
          </Box>
          <Stack direction='row' flexWrap='wrap' gap={1.5} sx={{ mt: 1, px: 1 }}>
            {(series as Array<{ id: string; label: string; color: string }>).map((quadrantSeries) => (
              <Stack key={quadrantSeries.id} direction='row' alignItems='center' spacing={0.5}>
                <Box sx={{ width: 12, height: 12, borderRadius: '3px', backgroundColor: quadrantSeries.color }} />
                <Typography variant='caption' color='text.secondary'>{quadrantSeries.label}</Typography>
              </Stack>
            ))}
          </Stack>
        </Box>
      )}
    </Paper>
  );
};
